import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, Area, AreaChart } from 'recharts';
import { Package, Truck, CheckCircle, Clock, DollarSign, TrendingUp, Users, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = 'https://api.ctexpresskh.com/api';
const token = () => localStorage.getItem('token');
const statusLabel = { pending:'Pending', cn_wh:'CN WH', in_transit:'In Transit', kh_wh:'KH WH', delivery:'Delivery', signed:'Delivered' };
const statusColor = { pending:'#f59e0b', cn_wh:'#3b82f6', in_transit:'#8b5cf6', kh_wh:'#06b6d4', delivery:'#f97316', signed:'#10b981' };

const StatCard = ({ label, value, color, icon, sub, onClick }) => (
  <div onClick={onClick} style={{ background:'#fff', borderRadius:12, padding:20, display:'flex', alignItems:'center', gap:14, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:'1px solid #f1f5f9', cursor:onClick?'pointer':'default', transition:'all 0.2s' }}
    onMouseEnter={e => onClick && (e.currentTarget.style.transform='translateY(-2px)')}
    onMouseLeave={e => onClick && (e.currentTarget.style.transform='translateY(0)')}>
    <div style={{ background:color+'18', borderRadius:12, padding:12, flexShrink:0 }}>{icon}</div>
    <div style={{ flex:1, minWidth:0 }}>
      <div style={{ fontSize:24, fontWeight:800, color:'#1e293b', lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:12, color:'#64748b', marginTop:3 }}>{label}</div>
      {sub && <div style={{ fontSize:11, color:color, fontWeight:700, marginTop:2 }}>{sub}</div>}
    </div>
  </div>
);

const StatusBadge = ({ status }) => (
  <span style={{ background:(statusColor[status]||'#94a3b8')+'22', color:statusColor[status]||'#64748b', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>
    {statusLabel[status]||status}
  </span>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [today, setToday] = useState(null);
  const [topCustomers, setTopCustomers] = useState([]);
  const [last7, setLast7] = useState([]);
  const [pieData, setPieData] = useState([]);

  useEffect(() => {
    fetch(API + '/dashboard', { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json()).then(d => {
        setStats(d.stats);
        setRecent(d.recent || []);
        setToday(d.today || null);
        setTopCustomers(d.top_customers || []);
        setLast7((d.last7 || []).map(x => ({
          day: new Date(x.day).toLocaleDateString('en-GB', {month:'short', day:'numeric'}),
          count: parseInt(x.count),
          fee: parseFloat(x.fee).toFixed(2)
        })));
        setPieData([
          { name:'Pending', value:parseInt(d.stats.total_pending)||0, color:'#f59e0b' },
          { name:'CN WH', value:parseInt(d.stats.total_cn_wh)||0, color:'#3b82f6' },
          { name:'In Transit', value:parseInt(d.stats.total_in_transit)||0, color:'#8b5cf6' },
          { name:'KH WH', value:parseInt(d.stats.total_kh_wh)||0, color:'#06b6d4' },
          { name:'Delivery', value:parseInt(d.stats.total_delivery)||0, color:'#f97316' },
          { name:'Delivered', value:parseInt(d.stats.total_signed)||0, color:'#10b981' },
        ].filter(x => x.value > 0));
      });
  }, []);

  if (!stats) return (
    <div style={{ padding:40, display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <div style={{ textAlign:'center', color:'#64748b' }}>
        <div style={{ fontSize:40, marginBottom:12 }}>⏳</div>
        <div>Loading...</div>
      </div>
    </div>
  );

  const barData = [
    { name:'Pending', count:parseInt(stats.total_pending)||0, fill:'#f59e0b' },
    { name:'CN WH', count:parseInt(stats.total_cn_wh)||0, fill:'#3b82f6' },
    { name:'In Transit', count:parseInt(stats.total_in_transit)||0, fill:'#8b5cf6' },
    { name:'KH WH', count:parseInt(stats.total_kh_wh)||0, fill:'#06b6d4' },
    { name:'Delivery', count:parseInt(stats.total_delivery)||0, fill:'#f97316' },
    { name:'Signed', count:parseInt(stats.total_signed)||0, fill:'#10b981' },
  ];

  return (
    <div style={{ padding:28, background:'#f8fafc', minHeight:'100vh' }}>
      {/* Header */}
      <div style={{ marginBottom:24, display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1 style={{ margin:0, fontSize:24, color:'#1e293b', fontWeight:800 }}>Dashboard</h1>
          <p style={{ margin:'3px 0 0', color:'#64748b', fontSize:13 }}>CT Express — Transportation Management</p>
        </div>
        <div style={{ background:'#fff', borderRadius:10, padding:'8px 16px', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', fontSize:13, color:'#64748b' }}>
          {new Date().toLocaleDateString('en-GB', {weekday:'long', year:'numeric', month:'long', day:'numeric'})}
        </div>
      </div>

      {/* Today Banner */}
      {today && (
        <div style={{ background:'linear-gradient(135deg,#f97316,#ea580c)', borderRadius:14, padding:'16px 24px', marginBottom:24, display:'flex', gap:32, color:'#fff' }}>
          <div>
            <div style={{ fontSize:11, opacity:0.8, fontWeight:600, textTransform:'uppercase', letterSpacing:1 }}>Today Shipments</div>
            <div style={{ fontSize:32, fontWeight:900, lineHeight:1.1 }}>{today.today_count}</div>
          </div>
          <div style={{ borderLeft:'1px solid rgba(255,255,255,0.3)', paddingLeft:32 }}>
            <div style={{ fontSize:11, opacity:0.8, fontWeight:600, textTransform:'uppercase', letterSpacing:1 }}>Today Revenue</div>
            <div style={{ fontSize:32, fontWeight:900, lineHeight:1.1 }}>${parseFloat(today.today_fee||0).toFixed(2)}</div>
          </div>
          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center' }}>
            <button onClick={() => navigate('/shipments')} style={{ background:'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.4)', color:'#fff', borderRadius:8, padding:'8px 16px', cursor:'pointer', fontWeight:600, fontSize:13 }}>
              View All →
            </button>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:14, marginBottom:24 }}>
        <StatCard label="Total Shipments" value={stats.total_all} color="#3b82f6" icon={<Package size={20} color="#3b82f6"/>} onClick={() => navigate('/shipments')} />
        <StatCard label="In Transit" value={stats.total_in_transit} color="#8b5cf6" icon={<Truck size={20} color="#8b5cf6"/>} sub="On the way" onClick={() => navigate('/shipments?status=in_transit')} />
        <StatCard label="KH Warehouse" value={stats.total_kh_wh} color="#06b6d4" icon={<Package size={20} color="#06b6d4"/>} sub="Ready for delivery" />
        <StatCard label="Delivered" value={stats.total_signed} color="#10b981" icon={<CheckCircle size={20} color="#10b981"/>} sub="Completed" />
        <StatCard label="Total Revenue" value={"$"+parseFloat(stats.total_revenue||0).toFixed(2)} color="#f97316" icon={<DollarSign size={20} color="#f97316"/>} sub="All shipments" />
        <StatCard label="Collected" value={"$"+parseFloat(stats.collected_revenue||0).toFixed(2)} color="#10b981" icon={<TrendingUp size={20} color="#10b981"/>} sub="Signed only" />
      </div>

      {/* Charts Row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:20, marginBottom:24 }}>
        {/* Status Bar Chart */}
        <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', gridColumn:'span 2' }}>
          <h3 style={{ margin:'0 0 16px', fontSize:15, color:'#1e293b', fontWeight:700 }}>Shipments by Status</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={barData} margin={{ top:0, right:0, left:-20, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize:10, fill:'#94a3b8' }} />
              <YAxis tick={{ fontSize:10, fill:'#94a3b8' }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius:8, border:'1px solid #e2e8f0', fontSize:12 }} />
              <Bar dataKey="count" radius={[6,6,0,0]}>
                {barData.map((e,i) => <Cell key={i} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart */}
        <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
          <h3 style={{ margin:'0 0 16px', fontSize:15, color:'#1e293b', fontWeight:700 }}>Distribution</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                  {pieData.map((e,i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius:8, border:'1px solid #e2e8f0', fontSize:11 }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize:10 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:200, color:'#94a3b8' }}>No data yet</div>}
        </div>
      </div>

      {/* Last 7 Days + Top Customers */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
        {/* Last 7 Days */}
        <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
          <h3 style={{ margin:'0 0 16px', fontSize:15, color:'#1e293b', fontWeight:700 }}>Last 7 Days Revenue</h3>
          {last7.length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={last7} margin={{ top:0, right:0, left:-20, bottom:0 }}>
                <defs>
                  <linearGradient id="feeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize:10, fill:'#94a3b8' }} />
                <YAxis tick={{ fontSize:10, fill:'#94a3b8' }} />
                <Tooltip contentStyle={{ borderRadius:8, border:'1px solid #e2e8f0', fontSize:11 }} formatter={(v) => ['$'+v, 'Revenue']} />
                <Area type="monotone" dataKey="fee" stroke="#f97316" strokeWidth={2} fill="url(#feeGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:160, color:'#94a3b8' }}>No data yet</div>}
        </div>

        {/* Top Customers */}
        <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
          <h3 style={{ margin:'0 0 16px', fontSize:15, color:'#1e293b', fontWeight:700 }}>Top Customers</h3>
          {topCustomers.length === 0 ? (
            <div style={{ textAlign:'center', color:'#94a3b8', padding:20 }}>No data yet</div>
          ) : topCustomers.map((c, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:i<topCustomers.length-1?'1px solid #f8fafc':'none' }}>
              <div style={{ width:28, height:28, borderRadius:'50%', background:['#3b82f6','#f97316','#10b981','#8b5cf6','#06b6d4'][i]+'20', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, color:['#3b82f6','#f97316','#10b981','#8b5cf6','#06b6d4'][i], flexShrink:0 }}>{i+1}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.receiver_name}</div>
                <div style={{ fontSize:11, color:'#94a3b8' }}>{c.receiver_phone}</div>
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#f97316' }}>${parseFloat(c.total_fee).toFixed(2)}</div>
                <div style={{ fontSize:10, color:'#94a3b8' }}>{c.total} shipments</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Shipments */}
      <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', overflow:'hidden' }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ margin:0, fontSize:15, color:'#1e293b', fontWeight:700 }}>Recent Shipments</h3>
          <button onClick={() => navigate('/shipments')} style={{ background:'none', border:'none', color:'#f97316', cursor:'pointer', fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
            View all <ArrowRight size={14} />
          </button>
        </div>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead style={{ background:'#f8fafc' }}>
            <tr>
              {['Tracking','Receiver','Phone','Method','Fee','Status','Date'].map(h => (
                <th key={h} style={{ padding:'9px 16px', textAlign:'left', color:'#64748b', fontWeight:600, borderBottom:'1px solid #e2e8f0', fontSize:12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recent.length === 0 ? (
              <tr><td colSpan={7} style={{ padding:32, textAlign:'center', color:'#94a3b8' }}>No shipments yet</td></tr>
            ) : recent.map(s => (
              <tr key={s.id} style={{ borderBottom:'1px solid #f8fafc', cursor:'pointer' }}
                onClick={() => navigate('/shipments/'+s.id)}
                onMouseEnter={e => e.currentTarget.style.background='#fafafa'}
                onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                <td style={{ padding:'9px 16px', fontWeight:700, color:'#f97316', fontSize:12 }}>{s.tracking_number}</td>
                <td style={{ padding:'9px 16px', fontWeight:500 }}>{s.receiver_name||'-'}</td>
                <td style={{ padding:'9px 16px', color:'#64748b', fontSize:12 }}>{s.receiver_phone}</td>
                <td style={{ padding:'9px 16px', textTransform:'uppercase', fontWeight:600, fontSize:11 }}>{s.shipping_method}</td>
                <td style={{ padding:'9px 16px', fontWeight:700 }}>${parseFloat(s.shipping_fee||0).toFixed(2)}</td>
                <td style={{ padding:'9px 16px' }}><StatusBadge status={s.status} /></td>
                <td style={{ padding:'9px 16px', color:'#94a3b8', fontSize:12 }}>{new Date(s.created_at).toLocaleDateString('en-GB')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
